import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
import './i18n/config';
import { CartProvider } from './context/CartContext';
import { B2BAuthProvider } from './context/B2BAuthContext';
import { AuthProvider } from './context/AuthContext';
import { ScrollToTop } from './components/ScrollToTop';
import { FloatingHomeButton } from './components/FloatingHomeButton';
import { CookieBanner } from './components/CookieBanner';
import { RootErrorBoundary, PageErrorBoundary } from './components/ErrorBoundary';

// Components needed for Home page - load immediately
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { USPSection } from './components/USPSection';
import { SegmentSection } from './components/SegmentSection';
import { CasesSection } from './components/CasesSection';
import { FAQSection } from './components/FAQSection';
import { ContactSection } from './components/ContactSection';
import { Footer } from './components/Footer';
import { FloatingOpMaat } from './components/FloatingOpMaat';
import { SEO, generateOrganizationSchema } from './components/SEO';

// Loading component for Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-earth-brown border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-500 font-sweet-sans">Laden...</p>
    </div>
  </div>
);

// Lazy load all pages that are not immediately visible
// Public pages
const Shop = lazy(() => import('./pages/Shop').then(m => ({ default: m.Shop })));
const ProductDetail = lazy(() => import('./pages/ProductDetail').then(m => ({ default: m.ProductDetail })));
const Checkout = lazy(() => import('./pages/Checkout').then(m => ({ default: m.Checkout })));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation').then(m => ({ default: m.OrderConfirmation })));
const CustomBasket = lazy(() => import('./pages/CustomBasket').then(m => ({ default: m.CustomBasket })));
const StoryPage = lazy(() => import('./pages/StoryPage').then(m => ({ default: m.StoryPage })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));
const MaintenancePage = lazy(() => import('./pages/MaintenancePage').then(m => ({ default: m.MaintenancePage })));
const MockPayment = lazy(() => import('./pages/MockPayment').then(m => ({ default: m.MockPayment })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));

// B2B pages
const B2BLogin = lazy(() => import('./pages/B2BLogin').then(m => ({ default: m.B2BLogin })));
const B2BRegister = lazy(() => import('./pages/B2BRegister').then(m => ({ default: m.B2BRegister })));
const B2BDashboard = lazy(() => import('./pages/B2BDashboard').then(m => ({ default: m.B2BDashboard })));
const B2BCatalog = lazy(() => import('./pages/B2BCatalog').then(m => ({ default: m.B2BCatalog })));
const B2BCart = lazy(() => import('./pages/B2BCart').then(m => ({ default: m.B2BCart })));
const B2BOrders = lazy(() => import('./pages/B2BOrders').then(m => ({ default: m.B2BOrders })));
const B2BOrderDetail = lazy(() => import('./pages/B2BOrderDetail').then(m => ({ default: m.B2BOrderDetail })));
const B2BQuotes = lazy(() => import('./pages/B2BQuotes').then(m => ({ default: m.B2BQuotes })));

// Admin pages - these are heavy and should definitely be lazy loaded
const AdminLogin = lazy(() => import('./pages/AdminLogin').then(m => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const DashboardHome = lazy(() => import('./pages/admin/DashboardHome').then(m => ({ default: m.DashboardHome })));
const HeroEditor = lazy(() => import('./pages/admin/HeroEditor').then(m => ({ default: m.HeroEditor })));
const USPEditor = lazy(() => import('./pages/admin/USPEditor').then(m => ({ default: m.USPEditor })));
const StoryEditor = lazy(() => import('./pages/admin/StoryEditor').then(m => ({ default: m.StoryEditor })));
const StoryPageEditor = lazy(() => import('./pages/admin/StoryPageEditor').then(m => ({ default: m.StoryPageEditor })));
const ContentEditor = lazy(() => import('./pages/admin/ContentEditor').then(m => ({ default: m.ContentEditor })));
const SegmentEditor = lazy(() => import('./pages/admin/SegmentEditor').then(m => ({ default: m.SegmentEditor })));
const CustomBasketPageEditor = lazy(() => import('./pages/admin/CustomBasketPageEditor').then(m => ({ default: m.CustomBasketPageEditor })));
const OrdersManager = lazy(() => import('./pages/admin/OrdersManager').then(m => ({ default: m.OrdersManager })));
const ProductsManager = lazy(() => import('./pages/admin/ProductsManager').then(m => ({ default: m.ProductsManager })));
const CategoriesManager = lazy(() => import('./pages/admin/CategoriesManager').then(m => ({ default: m.CategoriesManager })));
const FilterColorsManager = lazy(() => import('./pages/admin/FilterColorsManager').then(m => ({ default: m.FilterColorsManager })));
const SiteSettings = lazy(() => import('./pages/admin/SiteSettings').then(m => ({ default: m.SiteSettings })));
const BulkUploadManager = lazy(() => import('./pages/admin/BulkUploadManager').then(m => ({ default: m.BulkUploadManager })));
const MediaLibrary = lazy(() => import('./pages/admin/MediaLibrary').then(m => ({ default: m.MediaLibrary })));
const CasesEditor = lazy(() => import('./pages/admin/CasesEditor').then(m => ({ default: m.CasesEditor })));
const ColorsEditor = lazy(() => import('./pages/admin/ColorsEditor').then(m => ({ default: m.ColorsEditor })));
const FAQEditor = lazy(() => import('./pages/admin/FAQEditor').then(m => ({ default: m.FAQEditor })));
const ContactEditor = lazy(() => import('./pages/admin/ContactEditor').then(m => ({ default: m.ContactEditor })));
const CustomersManager = lazy(() => import('./pages/admin/CustomersManager').then(m => ({ default: m.CustomersManager })));
const DiscountsManager = lazy(() => import('./pages/admin/DiscountsManager').then(m => ({ default: m.DiscountsManager })));
const NewsletterManager = lazy(() => import('./pages/admin/NewsletterManager').then(m => ({ default: m.NewsletterManager })));
const B2BCustomerTypesManager = lazy(() => import('./pages/admin/B2BCustomerTypesManager').then(m => ({ default: m.B2BCustomerTypesManager })));
const B2BCustomersManager = lazy(() => import('./pages/admin/B2BCustomersManager').then(m => ({ default: m.B2BCustomersManager })));
const B2BQuotesManager = lazy(() => import('./pages/admin/B2BQuotesManager').then(m => ({ default: m.B2BQuotesManager })));
const B2BOrdersManager = lazy(() => import('./pages/admin/B2BOrdersManager').then(m => ({ default: m.B2BOrdersManager })));
const DataMigration = lazy(() => import('./pages/admin/DataMigration').then(m => ({ default: m.DataMigration })));
const CustomBasketsManager = lazy(() => import('./pages/admin/CustomBasketsManager').then(m => ({ default: m.CustomBasketsManager })));
const HealthLight = lazy(() => import('./pages/admin/HealthLight').then(m => ({ default: m.HealthLight })));
const AdminUsersManager = lazy(() => import('./pages/admin/AdminUsersManager').then(m => ({ default: m.AdminUsersManager })));
const ColorsSectionEditor = lazy(() => import('./pages/admin/ColorsSectionEditor').then(m => ({ default: m.ColorsSectionEditor })));

// Protected route component for role-based access
import { ProtectedRoute } from './components/ProtectedRoute';

// Test pages (can be removed in production)
const FloatingTest = lazy(() => import('./pages/FloatingTest').then(m => ({ default: m.FloatingTest })));
const HeaderTestPage = lazy(() => import('./pages/HeaderTestPage').then(m => ({ default: m.HeaderTestPage })));
const ErrorBoundaryTest = lazy(() => import('./pages/ErrorBoundaryTest').then(m => ({ default: m.ErrorBoundaryTest })));

// Site URL constant for canonical URLs
const SITE_URL = 'https://www.l-art.be';

// Home page component - loads immediately (no lazy loading)
const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="L'ART | Handgemaakte Manden uit België - Duurzaam & Artisanaal"
        description="Ontdek de tijdloze schoonheid van handgemaakte manden bij L'ART. Elk stuk is uniek en met liefde gemaakt in België uit 80% gerecycleerd katoen. Van boardroom naar atelier."
        keywords="handgemaakte manden, rieten manden, belgische manden, duurzame manden, gerecycleerd katoen, artisanale manden, opbergmanden, L'ART"
        url="/"
        canonical={`${SITE_URL}/`}
        schema={generateOrganizationSchema()}
      />
      <Header />
      <FloatingOpMaat variant="tab" />
      <main>
        <Hero />
        <USPSection />
        <SegmentSection />
        <CasesSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

// Wrapper components for key routes with error boundaries
const ShopWithErrorBoundary = () => (
  <PageErrorBoundary>
    <Shop />
  </PageErrorBoundary>
);

const ProductDetailWithErrorBoundary = () => (
  <PageErrorBoundary>
    <ProductDetail />
  </PageErrorBoundary>
);

const CheckoutWithErrorBoundary = () => (
  <PageErrorBoundary>
    <Checkout />
  </PageErrorBoundary>
);

const AdminDashboardWithErrorBoundary = () => (
  <PageErrorBoundary>
    <AdminDashboard />
  </PageErrorBoundary>
);

const B2BDashboardWithErrorBoundary = () => (
  <PageErrorBoundary>
    <B2BDashboard />
  </PageErrorBoundary>
);

function App() {
  return (
    <HelmetProvider>
      <RootErrorBoundary>
        <AuthProvider>
          <B2BAuthProvider>
            <div className="App">
              <BrowserRouter>
                <ScrollToTop />
                <FloatingHomeButton />
                <CartProvider>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Home - no lazy loading for fastest initial load */}
                      <Route path="/" element={<Home />} />
                      
                      {/* Test pages */}
                      <Route path="/floating-test" element={<FloatingTest />} />
                      <Route path="/header-test" element={<HeaderTestPage />} />
                    <Route path="/error-test" element={<ErrorBoundaryTest />} />
                    
                    {/* Public pages - key routes with error boundaries */}
                    <Route path="/shop" element={<ShopWithErrorBoundary />} />
                    <Route path="/shop/:categorySlug" element={<ShopWithErrorBoundary />} />
                    <Route path="/product/:productId" element={<ProductDetailWithErrorBoundary />} />
                    <Route path="/checkout" element={<CheckoutWithErrorBoundary />} />
                    
                    {/* Public pages - standard lazy loaded */}
                    <Route path="/maintenance" element={<MaintenancePage />} />
                    <Route path="/order-confirmation" element={<OrderConfirmation />} />
                    <Route path="/mock-payment" element={<MockPayment />} />
                    <Route path="/custom-basket" element={<CustomBasket />} />
                    <Route path="/verhaal" element={<StoryPage />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    
                    {/* B2B Routes - key routes with error boundaries */}
                    <Route path="/b2b/login" element={<B2BLogin />} />
                    <Route path="/b2b/register" element={<B2BRegister />} />
                    <Route path="/b2b/dashboard" element={<B2BDashboardWithErrorBoundary />} />
                    <Route path="/b2b/catalog" element={<B2BCatalog />} />
                    <Route path="/b2b/cart" element={<B2BCart />} />
                    <Route path="/b2b/orders" element={<B2BOrders />} />
                    <Route path="/b2b/orders/:orderId" element={<B2BOrderDetail />} />
                    <Route path="/b2b/quotes" element={<B2BQuotes />} />
                    
                    {/* Admin Routes - with error boundary */}
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/dashboard" element={<AdminDashboardWithErrorBoundary />}>
                      <Route index element={<DashboardHome />} />
                      <Route path="hero" element={<HeroEditor />} />
                      <Route path="usp" element={<USPEditor />} />
                      <Route path="story" element={<StoryEditor />} />
                      <Route path="story-page" element={<StoryPageEditor />} />
                      <Route path="segment" element={<SegmentEditor />} />
                      <Route path="custom-basket-page" element={<CustomBasketPageEditor />} />
                      <Route path="content" element={<ContentEditor />} />
                      <Route path="orders" element={<OrdersManager />} />
                      <Route path="custom-baskets" element={<CustomBasketsManager />} />
                      <Route path="customers" element={<CustomersManager />} />
                      <Route path="discounts" element={<DiscountsManager />} />
                      <Route path="newsletter" element={<NewsletterManager />} />
                      <Route path="b2b-types" element={<B2BCustomerTypesManager />} />
                      <Route path="b2b-customers" element={<B2BCustomersManager />} />
                      <Route path="b2b-quotes" element={<B2BQuotesManager />} />
                      <Route path="b2b-orders" element={<B2BOrdersManager />} />
                      <Route path="products" element={<ProductsManager />} />
                      <Route path="categories" element={<CategoriesManager />} />
                      <Route path="filter-colors" element={<FilterColorsManager />} />
                      <Route path="site-settings" element={<ProtectedRoute allowedRoles={['OWNER']}><SiteSettings /></ProtectedRoute>} />
                      <Route path="bulk-upload" element={<BulkUploadManager />} />
                      <Route path="media" element={<MediaLibrary />} />
                      <Route path="colors" element={<ColorsEditor />} />
                      <Route path="colors-section" element={<ColorsSectionEditor />} />
                      <Route path="cases" element={<CasesEditor />} />
                      <Route path="faqs" element={<FAQEditor />} />
                      <Route path="contact" element={<ContactEditor />} />
                      <Route path="data-migration" element={<ProtectedRoute allowedRoles={['OWNER']}><DataMigration /></ProtectedRoute>} />
                      <Route path="users" element={<ProtectedRoute allowedRoles={['OWNER']}><AdminUsersManager /></ProtectedRoute>} />
                      <Route path="health-light" element={<ProtectedRoute allowedRoles={['OWNER']}><HealthLight /></ProtectedRoute>} />
                    </Route>
                    
                    {/* 404 Catch-all Route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <CookieBanner />
              </CartProvider>
            </BrowserRouter>
            <Toaster />
          </div>
          </B2BAuthProvider>
        </AuthProvider>
      </RootErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
